import { Transaction } from '../../models/transaction';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CacheService } from '../cache';
import { ResourceService } from './resource';
import { Injectable } from '@angular/core';
import { BookingWalletApiResponse, CreateWalletDto, CreateWalletTransactionDto, WalletFilters, WalletResponse, WalletTransaction, WalletTransactionFilters, WalletTransactionListResponse } from '../../models/wallet';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TransactionService extends ResourceService<Transaction> {
private readonly BOOKING_WALLET_BASE = 'booking-wallet';

   /**
    * @constructor
    * Creates an instance of the TransactionService
    *
    * @param {HttpClient} http Injected instance of HttpClient
    * @param {CacheService} cache Injected instance of CacheService
  */
  constructor(
    protected http: HttpClient,
    protected cache: CacheService
  ) {
    super(http, cache, 'transactions');
  }

   /**
    * Sends a GET request to the /transactions/:id endpoint
    * @remarks
    * to get details of a transaction from the server
    * @param {string} id: unique identifier for the receipt to be downloaded
    * @returns {Observable} Returns an observable
  */
  getTransactionDetail(id: string) {
    return this.http.get(`${this.endpoint}/${id}`);
  }
   /**
    * Sends a GET request to the /transactions/:id/receipt endpoint
    * @remarks
    * to get receipt of a transaction from the server
    * @param {string} id: unique identifier for the receipt to be downloaded
    * @returns {Observable} Returns an observable
  */
  downloadReceipt(id: string) {
    return this.http.get(`${this.endpoint}/${id}/receipt`);
  }
/**
   * Creates a new booking wallet
   */
  createBookingWallet(data: CreateWalletDto): Observable<BookingWalletApiResponse> {
    return this.http.post<BookingWalletApiResponse>(
      this.BOOKING_WALLET_BASE,
      data
    );
  }



  /**
   * Creates a new booking wallet transaction
   */
  createBookingWalletTransaction(data: CreateWalletTransactionDto): Observable<any> {
    const url = `${this.BOOKING_WALLET_BASE}/transaction`;
    return this.http.post(url, data);
  }
  
  /**
   * Debits a wallet for a booking
   */
  debitWalletForBooking(data: any): Observable<any> {
    const url = `${this.BOOKING_WALLET_BASE}/debit`;
    return this.http.post(url, data);
  }



  /**
   * Fetches booking wallets (filtered or all)
   */
  getBookingWallets(filters: WalletFilters = {}): Observable<BookingWalletApiResponse> {
    let params = new HttpParams();

    if (filters.user)     params = params.set('user', filters.user);
    if (filters.business) params = params.set('business', filters.business);
    if (filters.currency) params = params.set('currency', filters.currency);
    if (filters.status)   params = params.set('status', filters.status);

    return this.http.get<BookingWalletApiResponse>(
      this.BOOKING_WALLET_BASE,
      { params }
    );
  }

  /**
   * Most convenient version for components — returns just the wallets array
   */
  getBookingWalletsArray(filters: WalletFilters = {}): Observable<WalletResponse[]> {
    return this.getBookingWallets(filters).pipe(
      map((response: { data: { bookingWallets: any; }; }) => response.data?.bookingWallets || [])
    );
  }
  getBookingWalletById(id: string): Observable<WalletResponse> {
    const url = `${this.BOOKING_WALLET_BASE}/${id}`;

    return this.http.get<BookingWalletApiResponse>(url).pipe(
      map(response => {
        // The real response is wrapped → extract the wallet object
        // Adjust this line if the backend returns the wallet directly (not wrapped)
        if (response.success === 1 && response.data?.bookingWallets?.length > 0) {
          return response.data.bookingWallets[0];
        }
        // Fallback / error case
        throw new Error(response.message || 'Failed to fetch wallet');
      })
    );
  }

  /**
   * Alternative version – returns full API response if you need success/message
   */
  getBookingWalletByIdFull(id: string): Observable<BookingWalletApiResponse> {
    const url = `${this.BOOKING_WALLET_BASE}/${id}`;
    return this.http.get<BookingWalletApiResponse>(url);
  }

  /**
 * Updates a booking wallet by its ID
 * PUT /api/v2/booking-wallet/:id
 *
 * @param id Wallet identifier (e.g. "699467c3057302a8345e6bc4")
 * @param updates Partial object with fields to update
 *                Supported fields (based on your API docs):
 *                - status?: string
 *                - credit_limit?: number
 *                - allowNegative?: boolean
 *
 * @returns Observable of the full updated wallet (or void if no body is returned)
 */
updateBookingWallet(
  id: string,
  updates: {
    status?: string;
    credit_limit?: number;
    allowNegative?: boolean;
    // add other updatable fields if they exist later
  }
): Observable<WalletResponse | void> {

  const url = `${this.BOOKING_WALLET_BASE}/${id}`;

  return this.http.put<BookingWalletApiResponse | void>(url, updates).pipe(
    map(response => {
      // Case 1: API returns 204 No Content → no body
      if (response === null || response === undefined) {
        return; // void
      }

      // Case 2: API returns the updated wallet (most common pattern)
      if ('success' in (response as any) && (response as any).success === 1) {
        // assuming same wrapper as list endpoint
        return (response as BookingWalletApiResponse).data?.bookingWallets?.[0] ||
               (response as any).data; // fallback
      }

      // Case 3: API returns the wallet directly
      return response as unknown as WalletResponse;
    })
  );
}

/**
 * Admin top-up: adds funds to a booking wallet

 */
topUpBookingWallet(
  walletId: string,
  amount: number,
  currency: string,
  owner?: { user?: string; business?: string }
): Observable<void> {
  if (amount <= 0 || !Number.isFinite(amount)) {
    return throwError(() => new Error('Invalid top-up amount'));
  }

  if (!currency) {
    return throwError(() => new Error('Currency is required for top-up'));
  }

  const url = `${this.BOOKING_WALLET_BASE}/${walletId}/top-up`;
  const body: any = { amount, currency };

  if (owner?.user) {
    body.user = owner.user;
  } else if (owner?.business) {
    body.business = owner.business;
  }
  // If neither is provided → backend may infer from wallet, or reject

  return this.http.put<void>(url, body);
}
/**
 * Fetches list of transactions for a wallet with optional filtering
 *
 * @param filters - Query filters (wallet_id is strongly recommended)
 * @returns Observable of transaction list response
 */
getWalletTransactions(
  filters: WalletTransactionFilters
): Observable<WalletTransactionListResponse> {
  if (!filters.wallet_id) {
    throw new Error('wallet_id is required for fetching transactions');
  }

  let params = new HttpParams();

  // Add all provided filters
  (Object.keys(filters) as Array<keyof WalletTransactionFilters>).forEach(key => {
    const value = filters[key];
    if (value !== undefined && value !== null) {
      params = params.set(key, String(value));
    }
  });

  const url = `${this.BOOKING_WALLET_BASE}/transaction`;

  return this.http.get<WalletTransactionListResponse>(url, { params });
}

/**
 * Convenience method: returns just the array of transactions
 * (most components prefer this)
 */


getWalletTransactionsArray(
  filters: WalletTransactionFilters
): Observable<WalletTransaction[]> {
  return this.getWalletTransactions(filters).pipe(
    map(response => response.data?.bookingWalletTransactions || [])
  );
}

/**
 * Convenience method: returns just the array of transactions
 * (most components prefer this)
 */


exportWalletTransactions(
  id: any,
  params?: any
) {
  const url = `${this.BOOKING_WALLET_BASE}/transaction/${id}/export`;

    // return this.http.get<WalletTransactionListResponse>(url, { params: filters });
 return this.http.get(`${this.BOOKING_WALLET_BASE}/transaction/${id}/export`, {params}).pipe(
    map((resp: any) => {
      this.cache.remove(`${this.BOOKING_WALLET_BASE}/transaction/${id}/export`);
      this.cache.remove(`${this.BOOKING_WALLET_BASE}`);
      return resp;
    })
  );

}
/**
 * Updates the default expiration time for payment links
 * POST /payment-link-expiration
 *


 @param value    Number of time units (e.g. 7, 30, 90)
  @param unit     Time unit — currently only "days" is supported
  @param isActive Whether this expiration rule should be enforced
  @returns Server response (often just success status/message)
 */
updatePaymentLinkExpiration(
  value: number,
  unit: 'minutes' | 'hours' | 'days' = 'days',
  isActive: boolean = true
): Observable<any> {
  if (value <= 0 || !Number.isInteger(value)) {
    return throwError(() => new Error('expirationValue must be a positive integer'));
  }

  const body = {
    expirationValue: value,
    expirationUnit: unit,
    isActive: isActive
  };


  // Option A – Most likely correct if other /booking-wallet calls use relative path
  const url = 'payment-link-expiration';

  // Option B – If the backend expects /api/v2/... prefix (common pattern)
  // const url = `/api/v2/payment-link-expiration`;

  // Option C – If you have a base path constant like BOOKING_WALLET_BASE
  // const url = `${this.BOOKING_WALLET_BASE.replace('booking-wallet', '')}payment-link-expiration`;



  return this.http.post<any>(url, body, {
    headers: {
      'x-requested-with': 'admin'

    }
  }).pipe(
    map(response => response?.data ?? response),

  );
}
/**
 * Convenience method: Returns only the expiration settings (cleaner for components)
 * Use this in most cases.
 */
getPaymentLinkExpiration(): Observable<any> {
  const url = 'payment-link-expiration';   // or 'api/v2/payment-link-expiration' if needed

  const headers = new HttpHeaders({
    'x-requested-with': 'admin'
  });

  return this.http.get<any>(url, { headers }).pipe(
    map(response => {
      if (response?.success !== 1) {
        throw new Error(response?.message || 'Failed to fetch payment link expiration time');
      }
      return response.data ?? response;   // Return data if wrapped, else full response
    })
  );
}
}
